import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

export interface CartItem {
  id: number
  product_id: number
  name: string
  price: number
  image: string
  quantity: number
  total: number
}

export const useCartStore = defineStore('cart', () => {
  const items = ref<CartItem[]>([])
  const isLoading = ref(false)

  // Computed properties
  const itemCount = computed(() => {
    return items.value.reduce((total, item) => total + item.quantity, 0)
  })

  const totalAmount = computed(() => {
    return items.value.reduce((total, item) => total + item.total, 0)
  })

  const isEmpty = computed(() => items.value.length === 0)

  // Actions
  const addToCart = (product: any, quantity: number = 1) => {
    
    const existingItem = items.value.find(item => item.product_id === product.id)
    
    if (existingItem) {
      // Update quantity if item already exists
      existingItem.quantity += quantity
      existingItem.total = existingItem.quantity * existingItem.price
    } else {
      // Add new item
      const newItem: CartItem = {
        id: Date.now(), // Temporary ID until saved to backend
        product_id: product.id,
        name: product.name,
        price: parseFloat(product.price),
        image: product.images && product.images.length > 0 ? product.images[0] : '/placeholder-product.jpg',
        quantity: quantity,
        total: parseFloat(product.price) * quantity
      }
      items.value.push(newItem)
    }
    
    // Save to localStorage
    saveToLocalStorage()
  }

  const removeFromCart = (εκδρομήId: number) => {
    const index = items.value.findIndex(item => item.product_id === εκδρομήId)
    if (index > -1) {
      items.value.splice(index, 1)
      saveToLocalStorage()
    }
  }

  const updateQuantity = (εκδρομήId: number, quantity: number) => {
    const item = items.value.find(item => item.product_id === εκδρομήId)
    if (item) {
      if (quantity <= 0) {
        removeFromCart(εκδρομήId)
      } else {
        item.quantity = quantity
        item.total = item.quantity * item.price
        saveToLocalStorage()
      }
    }
  }

  const clearCart = () => {
    items.value = []
    saveToLocalStorage()
  }

  const isInCart = (εκδρομήId: number) => {
    return items.value.some(item => item.product_id === εκδρομήId)
  }

  const getItemQuantity = (εκδρομήId: number) => {
    const item = items.value.find(item => item.product_id === εκδρομήId)
    return item ? item.quantity : 0
  }

  // LocalStorage functions
  const saveToLocalStorage = () => {
    try {
      localStorage.setItem('cart', JSON.stringify(items.value))
    } catch (error) {
    }
  }

  const loadFromLocalStorage = () => {
    try {
      const savedCart = localStorage.getItem('cart')
      if (savedCart) {
        items.value = JSON.parse(savedCart)
      }
    } catch (error) {
      items.value = []
    }
  }

  // Initialize cart from localStorage
  loadFromLocalStorage()

  return {
    items,
    isLoading,
    itemCount,
    totalAmount,
    isEmpty,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    isInCart,
    getItemQuantity,
    loadFromLocalStorage
  }
})
